import { useMutation, useQuery } from "@tanstack/react-query";
import apiClient from "@apis/index";
import Routes from "@apis/routes";
import { useDispatch, useSelector } from "react-redux";
import {
  setCompanyLocations,
  setUserInfo,
  useUserInfo,
} from "@redux/slices/userInfoSlice";
import { UserResponse, userUpdated } from "@src/types/user";
import { UserActivation } from "@src/types/UserActivation";
import { EmailVerification } from "@src/types/EmailVerification";
import { TokenVerification } from "@src/types/TokenVerification";
import { AccountDeleteDisclaimerResponse } from "@src/types/deleteAccountDisclaimer";
import Constants from "expo-constants";

export const useForgotPassword = () =>
  useMutation({
    mutationFn: (data: {
      username?: string;
      email?: string;
      recaptcha_token?: string;
    }) => apiClient.post(Routes.FORGOT_PASSWORD, data),
  });

export const useSignIn = () => {
  return useMutation({
    mutationFn: (
      data: (
        | {
            username: string;
            password: string;
          }
        | { public_token: string }
        | { iframe_token: string }
        | { auth_id: string }
      ) & {
        installation_identifier: string;
        config_version: string;
        device_info: any;
      }
    ) =>
      apiClient.post<UserResponse>(Routes.SIGN_IN, {
        ...data,
        config_version: process.env.EXPO_PUBLIC_APP_CONFIG_VERSION
          ? process.env.EXPO_PUBLIC_APP_CONFIG_VERSION
          : Constants?.expoConfig?.extra?.appConfigVersion,
        _t: Date.now(),
      }),
  });
};

export const useGetUser = (userId: number, setUserId?: any) => {
  const dispatch = useDispatch();
  const { getToken } = useUserInfo();
  const companyLocations = useSelector(
    (state: any) => state.userInfo.companyLocations
  );
  return useQuery({
    queryKey: ["GET_USER_DETAILS"],
    enabled: !!userId,
    cacheTime: 0,
    queryFn: () =>
      apiClient
        .get<userUpdated>(Routes.GET_USER_DETAILS(userId), {
          params: {
            config_version: process.env.EXPO_PUBLIC_APP_CONFIG_VERSION
              ? process.env.EXPO_PUBLIC_APP_CONFIG_VERSION
              : Constants?.expoConfig?.extra?.appConfigVersion,
          },
        })
        .then((data) => {
          dispatch(setUserInfo({ ...data.data.user, token: getToken() }));
          if (
            companyLocations.length === 0 &&
            data.data.user.company_locations.length > 0
          ) {
            dispatch(setCompanyLocations(data.data.user.company_locations));
          }
          if (setUserId) {
            setUserId(null);
          }
          return data;
        }),
    onError: (err) => {
      console.log("err", err);
    },
  });
};

export const useSignOut = () => {
  return useMutation({
    mutationFn: () => apiClient.delete(Routes.SIGN_OUT),
  });
};

export const useCheckUserActivation = (username: string, password: string) => {
  return useQuery({
    queryKey: ["CHECK_USER_ACTIVATION", username],
    enabled: !!username,
    retry: false,
    queryFn: () =>
      apiClient.get<UserActivation>(Routes.CHECK_USER_ACTIVATION, {
        params: {
          username,
          password,
        },
      }),
  });
};

export const useSendEmailVerification = (username: string) => {
  return useQuery({
    queryKey: ["EMAIL_VERIFICATION", username],
    enabled: !!username,
    retry: false,
    queryFn: () =>
      apiClient.get<EmailVerification>(Routes.EMAIL_VERIFICATION, {
        params: {
          username: username,
        },
      }),
  });
};

export const useTokenVerification = (
  token: string,
  enableAutoVerified?: boolean
) => {
  return useQuery({
    queryKey: ["TOKEN", token],
    enabled: !!token,
    queryFn: () =>
      apiClient.get<TokenVerification>(Routes.TOKEN_VERIFICATION, {
        params: {
          token: token,
          ...(enableAutoVerified && { enable_auto_verified: true }),
        },
      }),
  });
};

export const useUpdateUserPassword = () => {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (user: {
      reset_password_token: string;
      password: string;
      password_confirmation: boolean;
      enableAutoVerified: boolean;
    }) => {
      const response = await apiClient.put<TokenVerification>(
        Routes.UPDATE_PASSWORD,
        { user }
      );
      return response.data;
    },
    onSuccess(data) {
      dispatch(setUserInfo(data));
    },
  });
};
export const useJBTConnect = () => {
  return useMutation(
    (params: {
      auth_type: string;
      auth_key: string;
      installation_identifier: string;
    }) =>
      apiClient.get(Routes.JBT_CONNECT, {
        params: {
          auth_key: params.auth_key,
          auth_type: params.auth_type,
          installation_identifier: params.installation_identifier,
        },
      })
  );
};
export const useDeleteAccount = (id: number) =>
  useMutation({
    mutationFn: (data: { password: string }) =>
      apiClient.post(Routes.DELETE_ACCOUNT(id), data),
  });

export const useUpdateUser = (id: number) => {
  const dispatch = useDispatch();
  const { getToken } = useUserInfo();
  return useMutation({
    cacheTime: 0,
    mutationFn: (data: any) =>
      apiClient.put<userUpdated>(Routes.UPDATE_USER(id), {
        ...data,
        config_version: process.env.EXPO_PUBLIC_APP_CONFIG_VERSION
          ? process.env.EXPO_PUBLIC_APP_CONFIG_VERSION
          : Constants?.expoConfig?.extra?.appConfigVersion,
      }),
    onSuccess(data) {
      dispatch(setUserInfo({ ...data.data.user, token: getToken() }));
    },
  });
};

export const useDeleteAccountDisclaimer = () => {
  return useQuery({
    queryKey: ["DELETE_ACCOUNT_DISCLAIMER"],
    queryFn: () =>
      apiClient.get<AccountDeleteDisclaimerResponse>(
        Routes.DELETE_ACCOUNT_DISCLAIMER
      ),
  });
};

export const useDynamicRegister = () => {
  return useMutation({
    mutationFn: (data: any) =>
      apiClient.post(Routes.DYNAMIC_REGISTRATION, data),
  });
};

export const useIsUsernameAvailable = () => {
  return useMutation({
    mutationFn: (data: any) =>
      apiClient.post(Routes.IS_USERNAME_AVAILABLE, data),
  });
};

export const useUpdateUserOnJbt = (id: number) => {
  const dispatch = useDispatch();

  return useQuery({
    queryKey: ["UPDATE_USER_JBT", id],
    enabled: id > 0,
    queryFn: async () => {
      const response = await apiClient.get<userUpdated>(
        Routes.UPDATE_USER_JBT(id),
        {
          params: {
            config_version: process.env.EXPO_PUBLIC_APP_CONFIG_VERSION
              ? process.env.EXPO_PUBLIC_APP_CONFIG_VERSION
              : Constants?.expoConfig?.extra?.appConfigVersion,
          },
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      dispatch(setUserInfo(data.user));
    },
  });
};

export const useGetUserLoggedInTokens = (
  user_id: string | number,
  password: string
) => {
  return useQuery({
    queryKey: ["GET_USER_LOGGEDIN_TOKENS", user_id],
    enabled: !!user_id && !!password,
    retry: false,
    queryFn: () =>
      apiClient.get<UserActivation>(Routes.USER_TOKENS(user_id), {
        params: {
          password,
        },
      }),
  });
};

export const useDeleteSingleToken = (user_id: number, password: string) => {
  return useMutation({
    mutationFn: (data: { token: string }) =>
      apiClient.delete<userUpdated>(
        Routes.USER_TOKENS_DELETE_TOKEN(user_id, data?.token),
        {
          params: {
            password,
          },
        }
      ),
  });
};

export const useDeleteAllTokens = (user_id: number, password: string) => {
  return useMutation({
    mutationFn: () =>
      apiClient.delete<userUpdated>(
        `${Routes.USER_TOKENS_DELETE_ALL(user_id)}?password=${password}`
      ),
  });
};

export const useUserMfaSendOtp = () => {
  return useMutation({
    mutationFn: (data: any) =>
      apiClient.post<userUpdated>(Routes.MFA_OTP_SEND_OTP, {
        user_id: data.user_id,
        password: data.password,
      }),
  });
};

export const useUserMfaValidateOtp = () => {
  return useMutation({
    mutationFn: (data: any) =>
      apiClient.post<userUpdated>(Routes.MFA_OTP_VALIDATE_OTP, {
        user_id: data.user_id,
        otp: data.otp,
        password: data.password,
      }),
  });
};
